import { useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_FILE_MB,
  flattenedFilename,
  flattenPdfFormFields,
  validateFileSize,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

const UTILITY = getUtilityDefinition('flatten-pdf').analyticsKey

type Phase =
  | { kind: 'idle' }
  | { kind: 'ready'; file: File }
  | { kind: 'loading'; file: File }
  | {
      kind: 'success'
      file: File
      blob: Blob
      objectUrl: string
      totalPages: number
      fieldCount: number
      hasXfa: boolean
    }
  | { kind: 'error'; file: File | null; message: string; errorCode: string }

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex cursor-pointer select-none flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}

interface FileBadgeProps {
  file: File
  onClear: () => void
  disabled: boolean
}

function FileBadge({ file, onClear, disabled }: FileBadgeProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-primary"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>

      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}

function ScopeNotice() {
  const t = useT()

  return (
    <Card className="bg-accent/20">
      <CardContent className="space-y-2 p-4 text-sm">
        <p className="font-medium text-foreground">{t.flattenPdf.scopeTitle}</p>
        <p className="text-muted-foreground">{t.flattenPdf.scopeBody}</p>
      </CardContent>
    </Card>
  )
}

interface ResultPanelProps {
  file: File
  blob: Blob
  objectUrl: string
  totalPages: number
  fieldCount: number
  hasXfa: boolean
  onReset: () => void
}

function ResultPanel({
  file,
  blob,
  objectUrl,
  totalPages,
  fieldCount,
  hasXfa,
  onReset,
}: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = flattenedFilename(file.name)
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">
            {t.common.done}
          </span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.file}</span>
            <span className="truncate text-right font-medium text-foreground">{file.name}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.flattenPdf.totalPages}</span>
            <span className="font-medium text-foreground">{totalPages}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.flattenPdf.fieldsFlattened}</span>
            <span className="font-medium text-foreground">{fieldCount}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.size}</span>
            <span className="font-medium text-foreground">{formatBytes(blob.size)}</span>
          </div>
        </div>

        {fieldCount === 0 && (
          <p className="mb-4 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100">
            {t.flattenPdf.noFieldsNotice}
          </p>
        )}

        {hasXfa && (
          <p className="mb-4 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100">
            {t.flattenPdf.xfaNotice}
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.flattenPdf.downloadPdf}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.flattenPdf.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

interface ErrorPanelProps {
  message: string
  onRetry: () => void
}

function ErrorPanel({ message, onRetry }: ErrorPanelProps) {
  const t = useT()

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

export function FlattenPdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })

  const isLoading = phase.kind === 'loading'
  const hasFile = phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error'
  const currentFile = hasFile ? phase.file : null

  function handleFile(file: File) {
    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large')
      setPhase({
        kind: 'error',
        file,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }

    setPhase({ kind: 'ready', file })
  }

  function handleClear() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file && phase.errorCode !== 'client_too_large') {
      setPhase({ kind: 'ready', file: phase.file })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return

    const file = phase.file
    setPhase({ kind: 'loading', file })
    toolInvoke(UTILITY)
    const startMs = Date.now()

    try {
      const result = await flattenPdfFormFields(file)
      const objectUrl = URL.createObjectURL(result.blob)

      toolSuccess(UTILITY, Date.now() - startMs, {
        total_pages: result.totalPages,
        field_count: result.fieldCount,
        has_xfa: result.hasXfa,
        output_bytes: result.blob.size,
      })

      setPhase({
        kind: 'success',
        file,
        blob: result.blob,
        objectUrl,
        totalPages: result.totalPages,
        fieldCount: result.fieldCount,
        hasXfa: result.hasXfa,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message.toLowerCase() : ''
      const code = message.includes('encrypted') ? 'encrypted' : 'parse_error'
      toolError(UTILITY, code)
      setPhase({
        kind: 'error',
        file,
        errorCode: code,
        message: code === 'encrypted' ? t.flattenPdf.encrypted : t.flattenPdf.parseError,
      })
    }
  }

  if (phase.kind === 'success') {
    return (
      <ResultPanel
        file={phase.file}
        blob={phase.blob}
        objectUrl={phase.objectUrl}
        totalPages={phase.totalPages}
        fieldCount={phase.fieldCount}
        hasXfa={phase.hasXfa}
        onReset={handleClear}
      />
    )
  }

  return (
    <div className="space-y-5">
      {currentFile ? (
        <FileBadge file={currentFile} onClear={handleClear} disabled={isLoading} />
      ) : (
        <DropZone onFile={handleFile} disabled={isLoading} />
      )}

      <ScopeNotice />

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="w-full"
        disabled={phase.kind !== 'ready' || isLoading}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <>
            <Spinner />
            {t.flattenPdf.loading}
          </>
        ) : (
          t.flattenPdf.submit
        )}
      </Button>
    </div>
  )
}
